import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Plus,
  X,
  FileText,
  QrCode,
  MagnifyingGlass,
  ClipboardText,
} from "@phosphor-icons/react";

/**
 * QuickActionsFAB — floating orange button in the bottom-right that opens
 * a vertical menu of contextual actions. Rendered globally by AppLayout so
 * it's always one tap away.
 *
 * Actions vary by role:
 *   • admin / admin_kantor: Buat SPB, Cek Validasi, Cari SPB
 *   • petugas: Scan QR, Cari SPB
 *
 * Cari SPB uses a small inline input that on submit routes to
 *   /spb?q=<query>
 * (SPB List already reads `?q=` and filters.)
 */
export default function QuickActionsFAB() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current?.contains(e.target)) return;
      setOpen(false);
      setSearchOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!user) return null;

  const role = user.role;
  const actions = role === "petugas"
    ? [
      { icon: QrCode, label: "Scan QR", onClick: () => navigate("/loading") },
      { icon: MagnifyingGlass, label: "Cari SPB", onClick: () => setSearchOpen(true) },
    ]
    : [
      { icon: FileText, label: "Buat SPB", onClick: () => navigate("/spb/new") },
      { icon: ClipboardText, label: "Cek Validasi", onClick: () => navigate("/loading") },
      { icon: MagnifyingGlass, label: "Cari SPB", onClick: () => setSearchOpen(true) },
    ];

  const submitSearch = (e) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    navigate(`/spb?q=${encodeURIComponent(query)}`);
    setOpen(false);
    setSearchOpen(false);
    setQ("");
  };

  return (
    <div
      ref={ref}
      className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3"
      data-testid="quick-actions-fab"
    >
      {open && searchOpen && (
        <form
          onSubmit={submitSearch}
          className="bg-card border border-border rounded-xl shadow-2xl p-2 flex items-center gap-1 animate-in fade-in slide-in-from-bottom-2 duration-150"
          data-testid="fab-search-form"
        >
          <MagnifyingGlass size={16} className="text-muted-foreground ml-2" />
          <input
            autoFocus
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="No SPB, plat, produk…"
            className="bg-transparent outline-none px-2 py-2 text-sm w-56"
            data-testid="fab-search-input"
          />
          <button
            type="submit"
            className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold uppercase tracking-wider hover:bg-primary/90"
            data-testid="fab-search-submit"
          >
            Cari
          </button>
        </form>
      )}
      {open && !searchOpen && (
        <div className="flex flex-col items-end gap-2 animate-in fade-in slide-in-from-bottom-2 duration-150">
          {actions.map((a, i) => (
            <button
              key={a.label}
              type="button"
              onClick={() => a.onClick()}
              className="flex items-center gap-2 bg-card border border-border rounded-full shadow-lg pr-4 pl-3 py-2 hover:bg-secondary/60 transition-all hover:pr-5"
              style={{ animationDelay: `${i * 40}ms` }}
              data-testid={`fab-action-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="w-8 h-8 rounded-full bg-orange-100 text-primary flex items-center justify-center">
                <a.icon size={16} weight="bold" />
              </div>
              <span className="text-sm font-bold whitespace-nowrap">{a.label}</span>
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => { setOpen((v) => !v); setSearchOpen(false); }}
        aria-label={open ? "Tutup menu quick actions" : "Buka menu quick actions"}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all ${
          open ? "bg-secondary text-foreground rotate-45" : "bg-primary text-primary-foreground hover:scale-105"
        }`}
        data-testid="fab-toggle"
      >
        {open ? <X size={22} weight="bold" /> : <Plus size={22} weight="bold" />}
      </button>
    </div>
  );
}
